import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { FaFileExcel, FaSearch } from "react-icons/fa";

import { REACT_APP_API_URL } from "../../../../../env";

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getInitialFilters = () => {
  const today = new Date();
  return {
    fromDate: formatDateInput(new Date(today.getFullYear(), today.getMonth(), 1)),
    toDate: formatDateInput(today),
    hotelId: 0,
  };
};

const getHotelId = (hotel) =>  hotel?.PropertyID ||
  hotel?.HotelID ||
  hotel?.intHotelId ||
  hotel?.hotelId ||  0;

const getHotelName = (hotel) =>
  hotel?.UnitName ||
  hotel?.PropertyName ||
  hotel?.HotelName ||
  hotel?.hotelName ||
  hotel?.name ||
  "";

const ROW_CONTAINER_KEYS = [
  "data",
  "Data",
  "bookings",
  "Bookings",
];

const SUMMARY_KEYS = new Set([
  "message",
  "Message",
  "status",
  "Status",
  "success",
  "Success",
  "total",
  "Total",
  "count",
  "Count",
]);

const getRowsFromResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  for (const key of ROW_CONTAINER_KEYS) {
    const value = payload[key];
    if (Array.isArray(value)) return value;

    const nestedRows = getRowsFromResponse(value);
    if (nestedRows.length > 0) return nestedRows;
  }

  for (const [key, value] of Object.entries(payload)) {
    if (SUMMARY_KEYS.has(key)) continue;

    if (Array.isArray(value)) return value;

    const nestedRows = getRowsFromResponse(value);
    if (nestedRows.length > 0) return nestedRows;
  }

  return Object.keys(payload).some((key) => !SUMMARY_KEYS.has(key)) ? [payload] : [];
};

const flattenObject = (item, parentKey = "") => {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;

  return Object.entries(item).reduce((flattened, [key, value]) => {
    const flattenedKey = parentKey ? `${parentKey}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      return { ...flattened, ...flattenObject(value, flattenedKey) };
    }

    return { ...flattened, [flattenedKey]: value };
  }, {});
};

const normalizeRow = (row) => {
  if (row && typeof row === "object" && !Array.isArray(row)) return flattenObject(row);
  return { Value: row };
};

const toTitle = (key) =>
  String(key)
    .replace(/\./g, " ")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isValidDateValue = (value) => {
  if (!value || typeof value !== "string") return false;
  if (!/^\d{4}-\d{2}-\d{2}/.test(value) && !value.includes("T")) return false;

  return !Number.isNaN(new Date(value).getTime());
};

const isMoneyLikeKey = (key) =>
  /(amount|revenue|net|gross|tax|gst|charge|price|paid|refund|commission|discount|rent|fee|settlement)/i.test(
    key
  );

const formatNumber = (value) => Number(value).toFixed(2);
  

const formatCellValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (isValidDateValue(value)) {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  if (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) {
    const numberValue = Number(value);
    if (Number.isFinite(numberValue) && isMoneyLikeKey(key)) {
      return formatNumber(numberValue);
    }
  }

  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
};

const getExportValue = (key, value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  if (isValidDateValue(value)) return formatCellValue(key, value);
  if (isMoneyLikeKey(key) && Number.isFinite(Number(value))) return Number(value);

  return value;
};

const normalizeColumnKey = (key) =>
  String(key)
    .replace(/\(inr\)/gi, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const HIDDEN_COLUMN_KEYS = new Set([normalizeColumnKey("Int Booking ID")]);

const COLUMN_LABELS = {
  [normalizeColumnKey("Dtm Created On")]: "Booking Date",
  [normalizeColumnKey("Vch Booking Ref No")]: "BookingRefNo",
  [normalizeColumnKey("Vch Unit Name")]: "Hotel Name",
  [normalizeColumnKey("Vch Customer Name")]: "customer name",
  [normalizeColumnKey("Vch Mobile No")]: "Mobile No.",
  [normalizeColumnKey("Vch MRNumber")]: "MRNumber",
  [normalizeColumnKey("Dtm Check In")]: "CheckInDate",
  [normalizeColumnKey("Dtm Check Out")]: "CheckOutDate",
  [normalizeColumnKey("Total Room Amount")]: "Base Amount",
  [normalizeColumnKey("Num Initi Total Amount")]: "Total amount",
  [normalizeColumnKey("Total Tax Amount")]: "Tax amount",
  [normalizeColumnKey("Total Refund Amount")]: "Refund amount",
};

const COLUMN_SEQUENCE = [
  "Vch Booking Ref No",
  "Dtm Created On",
  "Vch Unit Name",
  "Vch Customer Name",
  "Vch Mobile No",
  "Vch MRNumber",
  "Dtm Check In",
  "Dtm Check Out",
  "Total Room Amount",
  "Total Tax Amount",
  "Num Initi Total Amount",
  "Total Refund Amount",
].map(normalizeColumnKey);

const getColumnSortValue = (key) => {
  const sequenceIndex = COLUMN_SEQUENCE.indexOf(normalizeColumnKey(key));
  return sequenceIndex === -1 ? Number.MAX_SAFE_INTEGER : sequenceIndex;
};

const getColumnHeader = (key) => {
  const configuredLabel = COLUMN_LABELS[normalizeColumnKey(key)];
  if (configuredLabel) return configuredLabel;

  const title = toTitle(key);
  return isMoneyLikeKey(key) ? `${title} (INR)` : title;
};

const PropertyNetRevenueTable = () => {
  const [filters, setFilters] = useState(getInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(getInitialFilters);
  const [hotels, setHotels] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const columns = useMemo(() => {
    const keySet = new Set();
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => keySet.add(key));
    });

    return Array.from(keySet)
      .filter((key) => !HIDDEN_COLUMN_KEYS.has(normalizeColumnKey(key)))
      .sort((first, second) => {
        const firstSortValue = getColumnSortValue(first);
        const secondSortValue = getColumnSortValue(second);

        if (firstSortValue !== secondSortValue) return firstSortValue - secondSortValue;
        return toTitle(first).localeCompare(toTitle(second));
      });
  }, [rows]);

  const fetchHotels = useCallback(async () => {
    try {
      const response = await axios.get(`${REACT_APP_API_URL}api/dashboard/admin/hotels`);
      const responseHotels = getRowsFromResponse(response.data)
        .map(normalizeRow)
        .map((hotel) => ({
          value: Number(getHotelId(hotel)) || 0,
          label: getHotelName(hotel) || `Hotel ${getHotelId(hotel)}`,
        }))
        .filter((hotel) => hotel.value || hotel.label)
        .sort((first, second) => first.label.localeCompare(second.label));

      setHotels(responseHotels);
    } catch (err) {
      console.error("Error fetching hotels:", err);
      setHotels([]);
    }
  }, []);

  const fetchReport = useCallback(async (reportFilters) => {
   
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}api/dashboard/admin/net-booking-revenue-report`,
        {
          FromDate: reportFilters.fromDate,
          ToDate: reportFilters.toDate,
          HotelID: Number(reportFilters.hotelId) || null,
        }
      );

      setRows(getRowsFromResponse(response.data).map(normalizeRow));
      setAppliedFilters(reportFilters);
    } catch (err) {
      console.error("Error fetching property net revenue report:", err);
      setRows([]);
      setError("Failed to load property net revenue report.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  useEffect(() => {
    const initialFilters = getInitialFilters();
    fetchReport(initialFilters);
  }, [fetchReport]);

  const handleFilterChange = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleSearch = () => {
    fetchReport(filters);
  };

  const handleExportExcel = () => {
    const exportRows = rows.map((row, index) => {
      const exportRow = { "#": index + 1 };
      columns.forEach((key) => {
        exportRow[getColumnHeader(key)] = getExportValue(key, row[key]);
      });
      return exportRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    worksheet["!cols"] = [{ wch: 8 }, ...columns.map(() => ({ wch: 22 }))];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Property Net Revenue");
    XLSX.writeFile(
      workbook,
      `property-net-revenue-${appliedFilters.fromDate}-to-${appliedFilters.toDate}.xlsx`
    );
  };

  return (
    <div>
      <div className="row g-3 mb-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">From Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.fromDate}
            onChange={(event) => handleFilterChange("fromDate", event.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">To Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.toDate}
            onChange={(event) => handleFilterChange("toDate", event.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Hotel</label>
          <select
            className="form-select"
            value={filters.hotelId}
            onChange={(event) =>
              handleFilterChange("hotelId", Number(event.target.value) || 0)
            }
          >
            <option value="0">All Hotels</option>
            {hotels.map((hotel) => (
              <option key={`${hotel.value}-${hotel.label}`} value={hotel.value}>
                {hotel.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-1">
          <button
            type="button"
            className="btn btn-primary w-100"
            onClick={handleSearch}
            disabled={loading}
            title="Show report"
            aria-label="Show report"
          >
            <FaSearch aria-hidden="true" />
          </button>
        </div>
        <div className="col-md-1">
          <button
            type="button"
            className="btn btn-outline-success w-100"
            onClick={handleExportExcel}
            disabled={loading || rows.length === 0}
            title="Export Excel"
            aria-label="Export Excel"
          >
            <FaFileExcel aria-hidden="true" />
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-muted small">
          Showing {rows.length} record{rows.length === 1 ? "" : "s"}
        </div>
        <div className="text-muted small">
          {appliedFilters.fromDate} to {appliedFilters.toDate}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-striped table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th scope="col">#</th>
              {columns.map((key) => (
                <th key={key} scope="col" className="text-nowrap">
                  {getColumnHeader(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={Math.max(columns.length + 1, 1)} className="text-center py-4">
                  Loading property net revenue report...
                </td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td>{rowIndex + 1}</td>
                  {columns.map((key) => (
                    <td key={key} className="text-nowrap">
                      {formatCellValue(key, row[key])}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={Math.max(columns.length + 1, 1)} className="text-center py-4">
                  No property net revenue records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PropertyNetRevenueTable;
